import {NotificationMessage} from '../../resources/tradeSummary.resource';

/**
 * Pure validation rules for the trade-summary multi-select actions
 * (multi-edit / recall / approve / reject).
 *
 * Extracted from the TradeSummaryPage click handlers so the selection-rule
 * matrix that the ~30 tradeSummary/multi-adjust cucumber scenarios exercised
 * through the full app can be unit-tested directly. The page component keeps
 * the ag-Grid mutation (per-row hint painting + column visibility); this
 * module only decides, from the selected rows, which banner message to show
 * and which rows should carry which hint message(s).
 */
export type MultiSelectAction = 'edit' | 'recall' | 'approve' | 'reject';

export interface MultiSelectRow {
    allowedTradeActions?: string[];
    custFirmId?: string;
    custTradeType?: string;
    dealNum?: string;
    evtTyp?: string;
    origTdate?: string;
    status?: string;
}

export interface MultiSelectValidation {
    /** banner message to surface ('' means the action modal may open) */
    message: string;
    /** uniqueKey (origTdate_evtTyp_dealNum) -> ordered list of row hint messages */
    hints: Record<string, string[]>;
    /** whether the hintError column should be shown */
    hasHints: boolean;
}

export const multiSelectKey = (row: MultiSelectRow): string =>
    [row.origTdate ?? '', row.evtTyp ?? '', row.dealNum ?? ''].join('_');

/**
 * The maximum number of rows a multi-edit may span. When the selected
 * original trade date is today the cap is 100, otherwise 5. Recall/approve/
 * reject always cap at 20.
 */
export const multiEditMaxRow = (isOrigTdateToday: boolean): number =>
    isOrigTdateToday ? 100 : 5;

export const getMultiSelectValidation = (
    action: MultiSelectAction,
    selected: MultiSelectRow[] = [],
    maxRow: number
): MultiSelectValidation => {
    const hintSets = new Map<string, Set<string>>();
    const addHint = (row: MultiSelectRow, hint: string): void => {
        const key = multiSelectKey(row);
        if (!hintSets.has(key)) {
            hintSets.set(key, new Set<string>());
        }
        hintSets.get(key).add(hint);
    };

    let isActionableValid = true;
    let isEvtTypeValid = true;
    let isLocalMMTrade = true;

    selected.forEach((row) => {
        const allowedTradeActions = row.allowedTradeActions ?? [];

        if (action === 'edit') {
            if (allowedTradeActions.length === 0) {
                isActionableValid = false;
                addHint(row, NotificationMessage.HINT_ACTION_ON_MORE_RECORDS);
            }
            if (row.evtTyp === 'RALC') {
                isEvtTypeValid = false;
                addHint(row, NotificationMessage.HINT_ACTION_ON_REVERSED_TRADES);
            }
        }

        if (action === 'recall') {
            if (
                allowedTradeActions.length === 0 ||
                !allowedTradeActions.includes('RECALL') ||
                row.status !== 'CO_APPROVAL_PENDING'
            ) {
                isActionableValid = false;
                addHint(row, NotificationMessage.HINT_ACTION_ON_MORE_RECORDS);
            }
        }

        if (action === 'approve' || action === 'reject') {
            if (
                allowedTradeActions.length === 0 ||
                !allowedTradeActions.includes('CONTRA_APPROVE') ||
                row.status !== 'CO_APPROVAL_PENDING'
            ) {
                isActionableValid = false;
                addHint(row, NotificationMessage.HINT_ACTION_ON_MORE_RECORDS);
            }
            if (row.custTradeType !== 'LOCAL_MM') {
                isLocalMMTrade = false;
                addHint(row, NotificationMessage.HINT_ACTION_LOCAL_MM);
            }
        }
    });

    const isEqualTo1 = selected.length === 1;
    const isMoreThanMax = selected.length > maxRow;
    const isCustFirmIdsUnique =
        new Set(selected.map((row) => row.custFirmId)).size === 1;

    let message = '';
    if (isEqualTo1) {
        message = NotificationMessage.EQUAL_TO_1;
    } else if (isMoreThanMax) {
        if (action === 'edit') {
            message =
                maxRow === 5
                    ? NotificationMessage.MORE_THAN_MAX_5
                    : NotificationMessage.MORE_THAN_MAX_100;
        } else {
            message = NotificationMessage.MORE_THAN_MAX_20;
        }
    } else if (action === 'edit' && !isEvtTypeValid) {
        message = NotificationMessage.ACTION_ON_REVERSED_TRADES;
    } else if (!isActionableValid) {
        message = NotificationMessage.ACTION_ON_MORE_RECORDS;
    } else if (
        (action === 'approve' || action === 'reject') &&
        !isCustFirmIdsUnique
    ) {
        message = NotificationMessage.ACTION_LOCAL_MM_WITH_DIFF_MPID;
    } else if (
        (action === 'approve' || action === 'reject') &&
        !isLocalMMTrade
    ) {
        message = NotificationMessage.ACTION_LOCAL_MM;
    }

    const hints: Record<string, string[]> = {};
    hintSets.forEach((set, key) => {
        hints[key] = Array.from(set);
    });

    const hasHints =
        action === 'edit'
            ? !isActionableValid || !isEvtTypeValid
            : action === 'recall'
                ? !isActionableValid
                : !isActionableValid || !isLocalMMTrade;

    return {message, hints, hasHints};
};
